import { ButtonHTMLAttributes, FC, memo, MouseEvent, ReactNode } from 'react';

import classNames from 'classnames';

import { dataTestValue } from '@telialt/utils/src/helpers';

export enum ButtonAppearance {
    Primary = 'primary',
    Secondary = 'secondary',
    Link = 'link',
}

export type ButtonProps = {
    appearance: ButtonAppearance | string;
    type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
    className?: string;
    isLonger?: boolean;
    isLoading?: boolean;
    disabled?: boolean;
    name?: string;
    value?: string;
    dataTest?: string;
    children?: ReactNode;
    href?: string;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    setRef?: (ref: HTMLButtonElement) => void;
    tabIndex?: number;
    ariaLabel?: string;
};

const ButtonComponent: FC<ButtonProps> = ({
    appearance,
    type = 'button',
    className,
    isLonger,
    isLoading,
    disabled = false,
    name,
    value,
    dataTest,
    children,
    href,
    onClick,
    setRef,
    tabIndex,
    ariaLabel,
}) => {
    const buttonClassNames = classNames(
        'btn',
        `btn-${appearance}`,
        isLonger && 'btn--longer',
        isLoading && 'is-loader disabled',
        disabled && !isLoading && 'disabled',
        className,
    );

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (onClick) {
            onClick(event);
        } else if (href) {
            window.location.href = href;
        }
    };

    const disableTab: boolean = !onClick && !href && type !== 'submit' && type !== 'reset' && tabIndex === undefined;

    return (
        <button
            className={buttonClassNames}
            type={type}
            disabled={disabled || isLoading}
            name={name}
            value={value}
            data-test={dataTestValue('action--button', name, dataTest)}
            ref={setRef}
            onClick={handleClick}
            tabIndex={disableTab ? -1 : tabIndex}
            aria-label={ariaLabel}
        >
            {children}
        </button>
    );
};

/**
 * @deprecated Use [Purpur]{@link https://purpur.telia.io/?path=/docs/forms-and-inputs-button--docs} Button component instead
 */
export const Button = memo(ButtonComponent);
